import { Component, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { HelpersService } from 'src/app/services/helpers.service';
import { PartnerService } from 'src/app/services/partner.service';
import { AppConfig } from 'src/app/app.config';
import { UpdateJobcardComponent } from 'src/app/components/modals/update-jobcard/update-jobcard.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Statuses } from 'src/app/enums/statuses.enum';
import { ServiceRequestService } from 'src/app/services/servicerequest.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

interface KanbanColumn {
  StatusID: number;
  StatusName: string;
  Information?: string;
}

interface Technician {
  ID: number;
  PartnerDescription: string;
  PartnerID: number;
  Firstname: string;
  Surname: string;
  IsActive: boolean;
  HoursPerDay: number;
  UserID: number;
  DailyValueGoal: number;
  UserSalutation: string;
  TechnicianIdentifier: string;
  POSEmployeeID: string;
  SortOrder: number;
}

interface JobCard {
  ID: number;
  ApprovedStatus: number;
  StatusID: number;
  StatusDescription: string;
  JobCardNo: string;
  TypeID: number;
  TypeDescription: string;
  Types: any[];
  Duration: number;
  BikeID: number;
  BikeDescription: string;
  CyclistID: number;
  CyclistDescription: string;
  DateCheckedIn: string;
  DateCheckedInEnd: string;
  DateBookedIn: string;
  DateRequiredBy: string;
  TechnicianID: number;
  TechnicianDescription: string;
  PaidWithStripe: boolean;
  PaidWithPOS: boolean;
  SentToPOS: boolean;
  BookedWith: string;
  Collection: boolean;
  DropOff: boolean;
  Linked: boolean;
  OriginalBookInDate: string;
  DaysSinceOriginalDate: number;
  BikeBay: string;
  BikeIsHere: boolean;
  GoalsOfFitSession: string;
  BikeTypeID: number;
  BikeType: string;
  PartnerID: number;
  PartnerDescription: string;
  PhoneNumber: string;
}

interface KanbanCell {
  jobCards: JobCard[];
  technicianId: number;
  statusId: number;
}

@Component({
  selector: 'app-workshop-services-kanban',
  templateUrl: './workshop-services-kanban.component.html',
  styleUrls: ['./workshop-services-kanban.component.scss']
})
export class WorkshopServicesKanbanComponent implements OnInit {

  statusList: any[] = [];
  jobCards: JobCard[] = [];
  filteredJobCards: JobCard[] = [];
  selectedPartnerIds: number[] = [];
  loading: boolean = false;
  filters: number[] = [];
  technicians: Technician[] = [];
  filteredTechnicians: Technician[] = [];

  // Filter properties
  selectedTechnicianIds: number[] = [];
  searchTerm: string = '';
  showAllTechnicians: boolean = true; // This will control the "All" chip


  public kanbanColumns: KanbanColumn[] = [
    {
      "StatusID": 15,
      "StatusName": "Booked In"
    },
    {
      "StatusID": 20,
      "StatusName": "Assigned to Technician"
    },
    {
      "StatusID": 25,
      "StatusName": "Started Working"
    },
    {
      "StatusID": 30,
      "StatusName": "Pause - awaiting client feedback"
    },
    {
      "StatusID": 31,
      "StatusName": "I Need Help"
    },
    {
      "StatusID": 35,
      "StatusName": "Pause - waiting for parts"
    },
    {
      "StatusID": 45,
      "StatusName": "Ready for Collection",
      "Information": "This column is only here to allow you to complete jobs, once completed they will not show when this is reloaded"
    }
  ];
  public sendComms: boolean = false;

  // Kanban grid data structure
  kanbanGrid: { [key: string]: JobCard[] } = {};

  constructor(
    private partnerService: PartnerService,
    private helpersService: HelpersService,
    private appConfig: AppConfig,
    private modalService: NgbModal,
    private serviceRequest: ServiceRequestService,
    private messageService: MessageService,
    private translate: TranslateService

  ) { }

  ngOnInit() {
    this.statusList = this.appConfig.getServiceStatusList().sort((a, b) => {
      if (a.index < b.index) return -1;
      else if (a.index > b.index) return 1;
      else return 0;
    });
    console.log('Status List:', this.statusList);
    this.kanbanColumns.forEach(column => {
      console.log('Setting StatusName for column:', column.StatusID);
      column.StatusName = this.appConfig.getStatusLabel(column.StatusID);
    });

    this.selectedPartnerIds.push(this.helpersService.getPartnerId());
    this.getTechnicians();
    this.loadData(1);
  }

  // Filter methods
  onTechnicianChipClick(technicianId: number | null) {
    if (technicianId === null) {
      // "All" chip clicked
      this.showAllTechnicians = true;
      this.selectedTechnicianIds = this.technicians.map(t => t.ID);
    } else {
      // Specific technician chip clicked
      this.showAllTechnicians = false;
      this.selectedTechnicianIds = this.technicians.filter(t => t.ID === technicianId).map(t => t.ID);
      // If no technicians selected, default back to "All"
      if (this.selectedTechnicianIds.length === 0) {
        this.showAllTechnicians = true;
        this.selectedTechnicianIds = this.technicians.map(t => t.ID);
      }

    }

    this.applyFilters();
  }

  onSearchChange() {
    this.applyFilters();
  }

  clearFilters() {
    this.selectedTechnicianIds = this.technicians.map(t => t.ID);
    this.searchTerm = '';
    this.showAllTechnicians = true;
    this.applyFilters();
  }

  applyFilters() {
    // Filter technicians
    if (this.showAllTechnicians) {
      this.filteredTechnicians = [...this.technicians];
      this.selectedTechnicianIds = this.technicians.map(t => t.ID);
    } else {
      this.filteredTechnicians = this.technicians.filter(t =>
        this.selectedTechnicianIds.includes(t.ID)
      );
    }

    // Filter job cards based on search term
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      this.filteredJobCards = this.jobCards.filter(jobCard =>
        jobCard.JobCardNo.toLowerCase().includes(searchLower) ||
        jobCard.CyclistDescription.toLowerCase().includes(searchLower) ||
        jobCard.BikeDescription.toLowerCase().includes(searchLower)
      );
    } else {
      this.filteredJobCards = [...this.jobCards];
    }

    // Re-initialize kanban grid with filtered data
    this.initializeKanbanGrid();
  }

  // Helper method to check if a technician chip is active
  isTechnicianChipActive(technicianId: number | null): boolean {
    if (technicianId === null) {
      return this.showAllTechnicians;
    }
    return !this.showAllTechnicians && this.selectedTechnicianIds.includes(technicianId);
  }

  // Helper method to get the count for each technician
  getTechnicianJobCount(technicianId: number | null): number {
    if (technicianId === null) {
      return this.jobCards.length;
    }
    return this.jobCards.filter(jc => jc.TechnicianID === technicianId).length;
  }

  initializeKanbanGrid() {
    // Initialize the grid with empty arrays for each cell
    this.filteredTechnicians.forEach(technician => {
      this.kanbanColumns.forEach(column => {
        const key = `${technician.ID}_${column.StatusID}`;
        this.kanbanGrid[key] = [];
      });
    });

    // Populate the grid with filtered job cards
    this.filteredJobCards.forEach(jobCard => {
      // Only show job cards for visible technicians
      if (this.selectedTechnicianIds.includes(jobCard.TechnicianID)) {
        const key = `${jobCard.TechnicianID}_${jobCard.StatusID}`;
        if (this.kanbanGrid[key]) {
          this.kanbanGrid[key].push(jobCard);
        }
      }
    });
  }

  getJobCardsForCell(technicianId: number, statusId: number): JobCard[] {
    const key = `${technicianId}_${statusId}`;
    return this.kanbanGrid[key] || [];
  }

  getCellDropListId(technicianId: number, statusId: number): string {
    return `cell-${technicianId}-${statusId}`;
  }

  getAllDropListIds(): string[] {
    const ids: string[] = [];
    this.filteredTechnicians.forEach(technician => {
      this.kanbanColumns.forEach(column => {
        ids.push(this.getCellDropListId(technician.ID, column.StatusID));
      });
    });
    return ids;
  }

  onDrop(event: CdkDragDrop<JobCard[]>) {
    const sourceKey = event.previousContainer.id.replace('cell-', '');
    const targetKey = event.container.id.replace('cell-', '');

    const [sourceTechnicianId, sourceStatusId] = sourceKey.split('-').map(Number);
    const [targetTechnicianId, targetStatusId] = targetKey.split('-').map(Number);

    if (event.previousContainer === event.container) {
      // Reordering within the same cell
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // Moving between different cells
      const jobCard = event.previousContainer.data[event.previousIndex];

      // Update the job card's properties
      jobCard.TechnicianID = targetTechnicianId;
      jobCard.StatusID = targetStatusId;

      // Find technician name for display
      const targetTechnician = this.technicians.find(t => t.ID === targetTechnicianId);
      if (targetTechnician) {
        jobCard.TechnicianDescription = targetTechnician.UserSalutation;
      }

      // Find status name for display
      const targetStatus = this.kanbanColumns.find(c => c.StatusID === targetStatusId);
      if (targetStatus) {
        jobCard.StatusDescription = targetStatus.StatusName;
      }

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      // Update the original jobCards array as well
      const originalJobCardIndex = this.jobCards.findIndex(jc => jc.ID === jobCard.ID);
      if (originalJobCardIndex !== -1) {
        this.jobCards[originalJobCardIndex] = { ...jobCard };
      }

      // Call API to update the backend
      this.updateJobCardStatus(jobCard, targetTechnicianId, targetStatusId);
    }
  }

  updateJobCardStatus(jobCard: JobCard, newTechnicianId: number, newStatusId: number) {
    // TODO: Implement API call to update job card status
    console.log('Updating job card:', {
      jobCardId: jobCard.ID,
      newTechnicianId: newTechnicianId,
      newStatusId: newStatusId,
      jobCard: jobCard
    });

    switch (newStatusId) {
      case Statuses.BOOKEDIN:
        this.serviceRequest
          .bikeReceived(jobCard.ID.toString(), this.sendComms)
          .subscribe((jobcard) => {
            this.messageService.add({
              life: 5000,
              detail: this.translate.instant(
                'bikeshop.modal.update_job_card.job_card.message_bike_received_and_assigned',
                { JobCardNo: jobcard.JobCardNo },
              ),
            });
          });
        break;
      case Statuses.WAITINGFORWORK:
        this.serviceRequest
          .waitingForWork(jobCard.ID.toString(), this.sendComms)
          .subscribe((jobcard) => {
            this.messageService.add({
              life: 5000,
              detail: this.translate.instant(
                'bikeshop.modal.update_job_card.job_card.message_jobcard_assigned',
                { JobCardNo: jobcard.JobCardNo },
              ),
            });
          });
        break;
      // case Statuses.ASSIGNED:
      //   this.serviceRequest
      //     .assignToTechnician(jobCard.ID.toString(), this.jobcard.TechnicianID)
      //     .subscribe((jobcard) => {
      //       this.messageService.add({
      //         life: 5000,
      //         detail: this.translate.instant(
      //           "bikeshop.modal.update_job_card.job_card.message_jobcard_assigned",
      //           { JobCardNo: jobcard.JobCardNo }
      //         ),
      //       });
      //       this.jobcard = jobcard;
      //       this.setDate();
      //       this.update.emit(this.jobcard);
      //       this.busySaving = false;
      //     });
      //   break;
      case Statuses.WORKINGON:
        this.serviceRequest
          .startWork(jobCard.ID.toString(), this.sendComms)
          .subscribe((jobcard) => {
            this.messageService.add({
              life: 5000,
              detail: this.translate.instant(
                'bikeshop.modal.update_job_card.job_card.message_job_commenced',
                { JobCardNo: jobcard.JobCardNo },
              ),
            });

          });
        break;
      case Statuses.WARRANTY:
        this.serviceRequest
          .startWarrantyWork(jobCard.ID.toString(), this.sendComms)
          .subscribe((jobcard) => {
            this.messageService.add({
              life: 5000,
              detail: this.translate.instant(
                'bikeshop.modal.update_job_card.job_card.message_warranty_job_commenced',
                { JobCardNo: jobcard.JobCardNo },
              ),
            });

          });
        break;
      case Statuses.READY:

        this.serviceRequest
          .readyForCollectionV2(jobCard.ID.toString(), this.sendComms)
          .subscribe(
            (jobcard) => {
              this.messageService.add({
                life: 5000,
                detail: this.translate.instant(
                  'bikeshop.modal.update_job_card.job_card.message_job_completed',
                  { JobCardNo: jobcard.JobCardNo },
                ),
              });


            },
            (err) => {
              console.log(err);
              this.messageService.add({
                life: 5000,
                detail: this.translate.instant(
                  'bikeshop.modal.update_job_card.job_card.message_job_completed_err',
                  { JobCardNo: jobCard.JobCardNo },
                ),
              });

            },
          );


        break;

      case Statuses.INEEDHELP:
        this.serviceRequest.iNeedHelp(jobCard.ID.toString()).subscribe((jobcard) => {

          this.messageService.add({
            life: 5000,
            detail: this.translate.instant(
              this.helpersService.getPartnerData() &&
                this.helpersService.getPartnerData().INeedHelpLabel
                ? 'bikeshop.modal.update_job_card.job_card.message_job_paused_custom'
                : 'bikeshop.modal.update_job_card.job_card.message_job_paused_i_need_help',
              {
                JobCardNo: jobcard.JobCardNo,
                CustomLabel:
                  this.helpersService.getPartnerData().INeedHelpLabel,
              },
            ),
          });

        });
        break;
      case Statuses.WAITINGPARTS:
        this.serviceRequest
          .waitingForParts(jobCard.ID.toString(), this.sendComms)
          .subscribe((jobcard) => {

            this.messageService.add({
              life: 5000,
              detail: this.translate.instant(
                'bikeshop.modal.update_job_card.job_card.message_job_paused_waiting_parts',
                { JobCardNo: jobcard.JobCardNo },
              ),
            });

          });
        break;
      case Statuses.WAITINGCLIENT:
        this.serviceRequest
          .waitingForClient(jobCard.ID.toString(), this.sendComms)
          .subscribe((jobcard) => {

            this.messageService.add({
              life: 5000,
              detail: this.translate.instant(
                'bikeshop.modal.update_job_card.job_card.message_job_paused_waiting_client',
                { JobCardNo: jobcard.JobCardNo },
              ),
            });

          });
        break;
      case Statuses.FITTINGCOMPLETED:
        this.serviceRequest
          .jobCompleted(jobCard.ID.toString(), this.sendComms)
          .subscribe((jobcard) => {


          });
        break;
      case Statuses.CANCELED:
        break;
    }
  }

  loadData(index: number) {
    this.loading = true;
    this.partnerService
      .getPartnerJobCardsMinimumV2(
        this.helpersService.getPartnerId(),
        this.statusList[index].id,
        this.selectedPartnerIds.join(','),
      )
      .subscribe((result) => {
        console.log('getPartnerJobCardsMinimumV2');
        console.log(JSON.stringify(result));

        this.jobCards =
          this.statusList[index].id === 100
            ? result.sort((x, y) => {
              if (x.DateCheckedIn < y.DateCheckedIn) return 1;
              if (x.DateCheckedIn > y.DateCheckedIn) return -1;
              return 0;
            })
            : result;

        this.loading = false;

        // Apply filters and initialize the kanban grid
        this.applyFilters();

        var items = result.map((x) => {
          return x.StatusID;
        });
        let uniqueItems = items
          .filter((v, i, a) => a.indexOf(v) === i)
          .sort((a, b) => {
            return a < b ? -1 : a > b ? 1 : 0;
          });
        this.filters = [0, ...uniqueItems];
      });
  }

  getTechnicians() {
    this.partnerService
      .getPartnerTechniciansV2(this.selectedPartnerIds.join(','))
      .subscribe((result) => {
        console.log('getPartnerTechniciansV2');
        console.log(result);
        this.technicians = result.sort((a, b) => a.SortOrder - b.SortOrder);

        // Initialize filters
        this.selectedTechnicianIds = this.technicians.map(t => t.ID);
        this.filteredTechnicians = [...this.technicians];

        console.log('technicians');
        console.log(JSON.stringify(this.technicians));

        // Initialize the kanban grid after loading technicians
        if (this.jobCards.length > 0) {
          this.applyFilters();
        }
      });
  }

  // Helper method to get technician name by ID
  getTechnicianName(technicianId: number): string {
    const technician = this.technicians.find(t => t.ID === technicianId);
    return technician ? technician.UserSalutation : 'Unknown';
  }

  // Helper method to check if technician has any job cards
  technicianHasJobCards(technicianId: number): boolean {
    return this.kanbanColumns.some(column =>
      this.getJobCardsForCell(technicianId, column.StatusID).length > 0
    );
  }

  getJobCardStatusClass(statusId: number): string {
    switch (statusId) {
      case 10: return 'status-scheduled';
      case 15: return 'status-booked-in';
      case 16: return 'status-booked-in';
      case 20: return 'status-assigned';
      case 25: return 'status-started';
      case 26: return 'status-warranty';
      case 30: return 'status-waiting-client';
      case 31: return 'status-help-needed';
      case 35: return 'status-waiting-parts';
      case 40: return 'status-completed';
      case 41: return 'status-completed';
      case 45: return 'status-ready';
      default: return '';
    }
  }

  onJobCardClick(jobCard: JobCard) {
    console.log('Job Card Clicked:', jobCard);
    const modalRef = this.modalService.open(UpdateJobcardComponent, {
      size: 'lg',
      backdrop: 'static',
      windowClass: 'jobcard-modal',
    });
    modalRef.componentInstance.jobcard = null;
    modalRef.componentInstance.jobcardID = jobCard.ID;
    modalRef.componentInstance.partnerID = jobCard.PartnerID;
    modalRef.result.then(
      (result) => { },
      (onrejected) => {
        this.loadData(1);
      },
    );

  }
}